/**
 * Portal Sidebar Navigation
 *
 * Customer-facing navigation with portal-specific routes.
 * Mirrors admin Sidebar.tsx structure with simplified nav items.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/auth/useAuth';
import { useStoredUser } from '@/services/auth/stored-user';
import { usePortalPermissions } from '@/hooks/auth/usePortalPermissions';
import {
  PORTAL_NAV_LABELS,
  isValidExperience,
  resolvePortalNavHref,
  usePortalHome,
} from '@/hooks/portal/usePortalHome';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SignOutButton } from '@/components/common/SignOutButton';
import {
  LayoutDashboard,
  FileText,
  Network,
  MonitorPlay,
  Users,
  Users2,
  BarChart3,
  Boxes,
  CreditCard,
  FolderKanban,
  Settings,
  X,
  Activity,
  User,
  ShieldAlert,
} from 'lucide-react';
import { BackToAdminLink } from './OrgSwitcher';

interface PortalSidebarProps {
  onClose?: () => void;
}

const navigation = [
  {
    key: 'dashboard',
    name: PORTAL_NAV_LABELS.dashboard,
    href: '/portal/dashboard',
    icon: LayoutDashboard,
    description: 'Overview & stats',
  },
  {
    key: 'sources',
    name: PORTAL_NAV_LABELS.sources,
    href: '/portal/sources',
    icon: MonitorPlay,
    description: 'Evergreen sources',
  },
  {
    key: 'projects',
    name: PORTAL_NAV_LABELS.projects,
    href: '/portal/projects',
    icon: FolderKanban,
    description: 'Investigations',
  },
  {
    key: 'content',
    name: PORTAL_NAV_LABELS.content,
    href: '/portal/content',
    icon: FileText,
    description: 'Browse content',
  },
  {
    key: 'patterns',
    name: PORTAL_NAV_LABELS.patterns,
    href: '/portal/patterns',
    icon: Network,
    description: 'Cultural patterns',
  },
  {
    key: 'entities',
    name: PORTAL_NAV_LABELS.entities,
    href: '/portal/entities',
    icon: Boxes,
    description: 'Entity intelligence',
  },
  {
    key: 'brand_safety',
    name: PORTAL_NAV_LABELS.brand_safety,
    href: '/portal/sentinel/flags',
    icon: ShieldAlert,
    description: 'Creator flag queue',
  },
  {
    key: 'flag_digests',
    name: PORTAL_NAV_LABELS.flag_digests,
    href: '/portal/sentinel/digests',
    icon: FileText,
    description: 'Daily flag roll-up',
  },
  {
    key: 'team',
    name: PORTAL_NAV_LABELS.team,
    href: '/portal/team',
    icon: Users,
    description: 'Manage members',
  },
  {
    key: 'usage',
    name: PORTAL_NAV_LABELS.usage,
    href: '/portal/usage',
    icon: BarChart3,
    description: 'Hours & limits',
  },
  {
    key: 'billing',
    name: PORTAL_NAV_LABELS.billing,
    href: '/portal/billing',
    icon: CreditCard,
    description: 'Plans & subscriptions',
  },
  {
    key: 'settings',
    name: PORTAL_NAV_LABELS.settings,
    href: '/portal/settings',
    icon: Settings,
    description: 'Domains & preferences',
  },
  {
    key: 'roster',
    name: PORTAL_NAV_LABELS.roster,
    href: '',
    icon: Users2,
    description: 'Creator roster',
  },
];

export function PortalSidebar({ onClose }: PortalSidebarProps) {
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const user = useStoredUser();
  const { isOrgAdmin } = usePortalPermissions();
  const config = user?.portalMemberExperience;
  const home = usePortalHome();

  // Restricted nav applies only to non-admin members of a configured org.
  // Absent/malformed config, or an admin, always gets the full nav.
  const visibleNavigation = (() => {
    if (isOrgAdmin || !isValidExperience(config)) {
      return navigation.filter((item) => item.key !== 'roster');
    }
    // Walk `config.nav` rather than filtering `navigation`, so the order the
    // org declares is the order the member sees. Filtering the module-level
    // array would silently impose its own ordering on every configured org.
    const seen = new Set<string>();
    const allowed = config.nav
      .filter((key) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((key) => navigation.find((item) => item.key === key))
      .filter((item): item is (typeof navigation)[number] => item !== undefined)
      .map((item) => ({ ...item, href: resolvePortalNavHref(item.key, config) ?? item.href }))
      .filter((item) => item.href !== '');
    // Never render an empty sidebar — a config that resolves to nothing
    // degrades to the full nav rather than stranding the user.
    return allowed.length > 0 ? allowed : navigation.filter((item) => item.key !== 'roster');
  })();

  return (
    <div className="flex h-full flex-col bg-card border-r border-border">
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-border">
        {/* The wordmark is the way home. A member whose nav omits the dashboard
            has no other route back to where they landed once they navigate
            away from it (CUR-1395). */}
        <NavLink
          to={home}
          onClick={onClose}
          title="Back to your home page"
          className="flex items-center space-x-2 rounded-md transition-opacity hover:opacity-80"
        >
          <Activity className="h-6 w-6 text-primary" />
          <span className="text-lg font-semibold">Portal</span>
        </NavLink>

        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="lg:hidden"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4">
        {visibleNavigation.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== '/portal/dashboard' && location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <item.icon className="h-4 w-4" />
              <div className="flex-1">
                <div>{item.name}</div>
                <div className="text-xs opacity-70">{item.description}</div>
              </div>
            </NavLink>
          );
        })}
      </nav>

      <Separator />

      {/* User section */}
      <div className="p-4 space-y-4">
        {user && (
          <NavLink
            to="/portal/profile"
            onClick={onClose}
            className="flex items-center space-x-3 p-2 rounded-lg bg-accent/50 hover:bg-accent transition-colors cursor-pointer"
          >
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground">
              {user.imageUrl ? (
                <img src={user.imageUrl} alt="Your avatar" className="h-full w-full object-cover" />
              ) : (
                <User className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user.displayName ||
                 (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                 user.firstName ||
                 user.emailAddress ||
                 'User'}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {user.organizationRole || 'Member'}
              </p>
            </div>
          </NavLink>
        )}

        {isSuperAdmin && <BackToAdminLink />}

        <SignOutButton />

        <div className="text-center">
          <Badge variant="secondary" className="text-xs">
            {isSuperAdmin ? 'Admin Portal' : 'Portal'}
          </Badge>
        </div>
      </div>
    </div>
  );
}
